import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Quote, Save, Plus, Trash2 } from "lucide-react";

interface ValueQuote {
  id: number;
  text: string;
  active: boolean;
}

export default function AdminQuotesScreen() {
  const [quotes, setQuotes] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    try {
      const response = await fetch("/api/value-quotes");
      if (response.ok) {
        const data: ValueQuote[] = await response.json();
        setQuotes(data.map((q) => q.text));
      } else {
        toast({
          title: "Error",
          description: "Failed to fetch quotes",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error fetching quotes:", error);
      toast({
        title: "Error",
        description: "Failed to fetch quotes",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const saveQuotes = async () => {
    setSaving(true);
    try {
      const filteredQuotes = quotes.filter(q => q.trim().length > 0);
      
      const response = await fetch("/api/value-quotes", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ quotes: filteredQuotes }),
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Quotes saved successfully",
        });
        await fetchQuotes(); // Refresh the quotes
      } else {
        toast({
          title: "Error",
          description: "Failed to save quotes",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error saving quotes:", error);
      toast({
        title: "Error",
        description: "Failed to save quotes",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const updateQuote = (index: number, value: string) => {
    const updated = [...quotes];
    updated[index] = value;
    setQuotes(updated);
  };

  const addQuote = () => {
    setQuotes([...quotes, ""]);
  };

  const removeQuote = (index: number) => {
    setQuotes(quotes.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading quotes...</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Quote className="w-8 h-8 text-primary" />
            Value Quote Editor
          </h1>
          <p className="text-gray-400 mt-2">
            Manage anonymized value quotes for PowerPoint presentations
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={addQuote}
            variant="outline"
            className="border-[#334155] text-gray-300 hover:bg-forestBlack"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Quote
          </Button>
          <Button
            onClick={saveQuotes}
            disabled={saving}
            className="bg-primary text-black hover:bg-[#2DD4BF]"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save Quotes"}
          </Button>
        </div>
      </div>

      <Card className="bg-forestBlack border-[#334155]">
        <CardHeader>
          <CardTitle className="text-primary">Quote Management</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {quotes.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              No quotes found. Click "Add Quote" to get started.
            </div>
          ) : (
            quotes.map((quote, index) => (
              <div key={index} className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-300">
                    Quote {index + 1}
                  </label>
                  <Button
                    onClick={() => removeQuote(index)}
                    variant="ghost"
                    size="sm"
                    className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                <Textarea
                  value={quote}
                  onChange={(e) => updateQuote(index, e.target.value)}
                  placeholder="Enter a value quote..."
                  className="bg-[#0F172A] border-[#334155] text-white placeholder-gray-500 resize-none"
                  rows={3}
                />
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Card className="bg-forestBlack border-[#334155]">
        <CardHeader>
          <CardTitle className="text-primary">Guidelines</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-gray-300">
          <div className="flex items-start gap-2">
            <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
            <p>Keep quotes anonymized - no company names or specific details</p>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
            <p>Focus on quantifiable business impact and outcomes</p>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
            <p>Use compelling language that resonates with finance leaders</p>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
            <p>Quotes are automatically available for PowerPoint exports</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}